
import React, { useState, useEffect, useRef } from "react";
import { TbRefresh } from "react-icons/tb";
import axios from "axios";
import { BaseUrl } from "../../../../../../api/AxiosApi";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import DownloadJpegButton from "../../../../../../components/downloadJpegButton/DownloadJpegButton";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

export function getDateTimeForDashboardTab1(date = new Date()) {
    const formattedCurrentDate = date.toISOString().split("T")[0];
    const oneWeekAgo = new Date(date);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 0);
    const formattedOneWeekAgo = oneWeekAgo.toISOString().split("T")[0];
    return {
        Today: formattedCurrentDate,
        LastWeek: formattedOneWeekAgo,
    };
}

const METRIC_OPTIONS = [
    { key: "Voltage_R", label: "Voltage R" },
    { key: "Voltage_Y", label: "Voltage Y" },
    { key: "Voltage_B", label: "Voltage B" },
    { key: "I_R", label: "I R" },
    { key: "I_Y", label: "I Y" },
    { key: "I_B", label: "I B" },
    { key: "KW", label: "Active Power [KW]" },
    { key: "HZ", label: "HZ" }
];

function UserAnalyticsTab2() {
    const chartRef = useRef();
    const [inverters, setInverters] = useState([]);
    const [compareInverters, setCompareInverters] = useState([]);
    // const [selectedMetrics, setSelectedMetrics] = useState([METRIC_OPTIONS[0].key]); 
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [fromDate, setFromDate] = useState(getDateTimeForDashboardTab1().LastWeek);
    const [toDate, setToDate] = useState(getDateTimeForDashboardTab1().Today);
    const [loading, setLoading] = useState(false);

    // Credentials used in both API calls
    const credentials = {
        LOGIN_ID: sessionStorage.getItem("LoginID"),
        LOC_CODE: sessionStorage.getItem("LCODE"),
        PASS_KEY: 'XYZ',
        WEB_KEY: "WEBXYZ321",
        TYP: "I",
    };

    // Fetch Inverter list for user
    const fetchInverters = async () => {
        try {
            const res = await axios.get(`${BaseUrl}CMN_07_Super/GET_FEEDER_LIST`, { params: credentials });
            setInverters(res.data.FEED || []);
            setChartData(null);
        } catch (err) {
            console.error("Inverter API Error:", err);
        }
    };

    const toggleCompareInverter = (inv) => {
        setCompareInverters((prev) =>
            prev.some((c) => c.feeder_id === inv.feeder_id)
                ? prev.filter((c) => c.feeder_id !== inv.feeder_id)
                : [...prev, inv]
        );
    };

    // Toggle metrics like checkboxes
    const toggleMetric = (key) => {
        setSelectedMetrics((prev) =>
            prev.includes(key)
                ? prev.filter(m => m !== key)
                : [...prev, key]
        );
    };

    // Fetch chart data (draws all selected metrics for each checked inverter)
    const fetchChartData = async () => {
        if (compareInverters.length === 0 || selectedMetrics.length === 0) {
            alert("Select at least one inverter and one metric.");
            return;
        }
        setLoading(true);
        try {
            let datasets = [];
            let labels = [];
            for (const inv of compareInverters) {
                // Use POSTMAN's param names (DT1, DT2, LOGIN_ID, PASS_KEY, WEB_KEY, MTRNO)
                const res = await axios.post(`${BaseUrl}CMN_08InvLog/post_INV_LOG`, new URLSearchParams({
                    LOGIN_ID: credentials.LOGIN_ID,
                    PASS_KEY: credentials.PASS_KEY,
                    WEB_KEY: credentials.WEB_KEY,
                    MTRNO: inv.feeder_id,
                    DT1: fromDate,
                    DT2: toDate,
                }));
                const apiData = res.data.data || [];
                if (labels.length === 0 && apiData.length) {
                    labels = apiData.map(d => d.DT); // X axis: datetime string
                }
                selectedMetrics.forEach((metricKey, i) => {
                    const values = apiData.map(d => parseFloat(d[metricKey] ?? 0));
                    datasets.push({
                        label: `${inv.name} - ${METRIC_OPTIONS.find(m => m.key === metricKey).label}`,
                        data: values,
                        borderColor: "#" + ((Math.abs((parseInt(inv.feeder_id, 36) + i * 33333)) % 0xFFFFFF).toString(16).padStart(6, "0")),
                        fill: false,
                        tension: 0.3,
                    });
                });
            }
            setChartData(datasets.length ? { labels, datasets } : null);
        } catch (err) {
            console.error("Chart API Error:", err);
            setChartData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInverters();
    }, []);

    return (
        <div style={{ padding: "10px" }}>
            <div style={{
                borderTop: "1px solid #ccc", borderRadius: "6px", padding: "8px",
                backgroundColor: "white", boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
            }}>
                <h5 style={{ margin: 0 }}>Analytics</h5>
            </div>
            {/* FLEX LAYOUT: Inverters | Select Type (metric checkboxes) | Compare Inverters */}
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: 10 }}>
                {/* Inverters (1st column) */}
                <div style={panelStyle}>
                    <h6>Inverters</h6>
                    <ul style={listStyle}>
                        {inverters.map((inv) => (
                            <li key={inv.feeder_id} style={listItem}>
                                <input
                                    type="checkbox"
                                    checked={compareInverters.some((c) => c.feeder_id === inv.feeder_id)}
                                    onChange={() => toggleCompareInverter(inv)}
                                />
                                <span style={nameStyle}>{inv.name}</span>
                                <span style={capStyle}>[{inv.kwp}kWp]</span>
                            </li>
                        ))}
                    </ul>
                </div>

                 {/* Compare Inverters (3rd column) */}
                 <div style={panelStyle}>
                    <h6>Compare Inverters</h6>
                    <ul style={listStyle}>
                        {compareInverters.map((c) => (
                            <li key={c.feeder_id} style={listItem}>
                                <span style={nameStyle}>{c.name}</span>
                                <span style={capStyle}>{c.kwp}kWp</span>
                                <button style={removeBtn} onClick={() => toggleCompareInverter(c)}>
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Select Type (metrics) (2nd column) */}
                <div style={panelStyle}>
                    <h6>Select Type</h6>
                    <ul style={listStyle}>
                        {METRIC_OPTIONS.map((mt) => (
                            <li key={mt.key} style={listItem}>
                                <input
                                    type="checkbox"
                                    checked={selectedMetrics.includes(mt.key)}
                                    onChange={() => toggleMetric(mt.key)}
                                />
                                <span style={nameStyle}>{mt.label}</span>
                            </li>
                        ))}
                    </ul>
                </div>
               
            </div>
            {/* Filters */}
            <div style={filterStyle}>
                <label style={labelStyle}>From:</label>
                <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
                <label style={labelStyle}>To:</label>
                <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
                <TbRefresh
                    style={{ fontSize: "1.5rem", cursor: "pointer" }}
                    onClick={fetchChartData}
                    title="Refresh Chart"
                />
                <DownloadJpegButton targetRef={chartRef} fileName={`Parameter Comparison.jpeg`} />
            </div>
            {/* Chart */}
            {chartData && (
                <div
                    style={{
                        marginTop: 12,
                        backgroundColor: "white",
                        borderRadius: 8,
                        padding: 10,
                        height: 300,
                        width: "100%",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                    }}

                    ref={chartRef} 
                >
                    <Line
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                                legend: { position: "top", labels: { font: { size: 10 } } },
                            },
                            scales: {
                                x: { ticks: { font: { size: 11 } } },
                                y: { ticks: { font: { size: 11 } } },
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
}

/* Style objects same as before */
const panelStyle = {
    flex: 1,
    minWidth: 200,
    border: "1px solid #ccc",
    borderRadius: 6,
    padding: 10,
    maxHeight: 170,
    overflowY: "auto",
    backgroundColor: "white",
    boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)",
};

const listStyle = { listStyle: "none", padding: 0, margin: 0 };
const listItem = { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 };
const nameStyle = { fontSize: 13 };
const capStyle = { color: "#888", fontSize: 12 };
const removeBtn = {
    marginLeft: "auto",
    background: "#ff5252",
    color: "white",
    border: "none",
    padding: "2px 6px",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 11,
};

const filterStyle = {
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    backgroundColor: "#f9f9f9",
    padding: 10,
    borderRadius: 6,
    boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
};

const labelStyle = { fontSize: 14, fontWeight: 500 };
const inputStyle = {
    fontSize: 14,
    borderRadius: 6,
    border: "1px solid #ccc",
    padding: "4px 10px",
    height: 32,
};

export default UserAnalyticsTab2;





